import classnames from "classnames";
import { FunctionComponent, useCallback, useMemo, useRef, useState } from "react";
import { PebbleList } from "@ppb/the-wall-web";
import { BlurbBar } from "../../../../BlurbBar/BlurbBar.web";
import { PlaceFooter } from "../../PlaceFooter/PlaceFooter.web";
import { BetslipPebble, SportsbookPlacePanelViewModel } from "./SportsbookPlacePanel.types";
import { usePebblesViewModel } from "./view-model-builder";
import { PEBBLE_RESOLVERS } from "./pebbles-resolver.web";
import styles from "./SportsbookPlacePanel.web.css";

export const SportsbookPlacePanel: FunctionComponent<SportsbookPlacePanelViewModel> = (props) => {
  const {
    i18n,
    isPanelDisabled,
    isPlaceDisabled,
    notifications,
    footerPrefix,
    termsUrl,
    hasFreeBets,
    isFreeBetsSelected,
    isFreeBetsDisabled,
    freeBetsAlertMessage,
    isSummaryDisabled,
    balanceAfterBet,
    totalReturns,
    totalOriginalReturns,
    isOddsBoosted,
    isOddsMovementOn,
    showAcceptOddsMovementAlert,
    oddsMovementLabels,
    hasCTALoading,
    hasPlaceError,
    isPlaceBlocked,
    placeBtnLabel,
    placeBtnSecondaryLabel,
    placeBtnLoadingLabel,
    reversePlaceBtnLabels,
    isLoggedIn,
    secondaryButton,
    onFreeBetsChange,
    onFreeBetsRemovePress,
    onRemoveAllClick,
    onPlaceClick,
    hasMarketBlurbs,
    onTabSwitch,
    onOddsMovementPreferencesChange,
    isDesktop,
  } = props;

  const scrollableRef = useRef<HTMLDivElement>(null);

  const { headers, contents } = usePebblesViewModel(props, PEBBLE_RESOLVERS);
  const [selectedId, setSelectedId] = useState<BetslipPebble | undefined>(undefined);

  const activePebbleId = selectedId && headers.some((header) => header.id === selectedId) ? selectedId : headers[0]?.id;

  const isSingleContent = contents?.length === 1;

  const onPebbleClick = useCallback(
    (tabId: string) => {
      const selectedHeader = headers.find((header) => header.id === tabId);

      if (!selectedHeader) {
        return;
      }

      setSelectedId(selectedHeader.id);
      onTabSwitch?.(selectedHeader.id, selectedHeader.title);
      scrollableRef.current?.scrollTo({ top: 0 });
    },
    [headers, onTabSwitch],
  );

  const content = useMemo(() => {
    if (!onTabSwitch || headers.length === 0 || contents?.length === 0) {
      return <></>;
    }

    if (isSingleContent) {
      return <div className={styles.singlesScrollable}>{contents[0].content}</div>;
    }

    if (!activePebbleId) {
      return <></>;
    }

    const activePebbleContent = contents.find((pebble) => pebble.id === activePebbleId)?.content;

    return (
      <>
        <div className={styles.pebbleListContainer}>
          <PebbleList
            items={headers.map((header) => ({ id: header.id, text: header.title }))}
            selectedPebble={activePebbleId}
            onPebbleClick={onPebbleClick}
            isDesktopLayout={Boolean(isDesktop)}
            showDesktopArrows={Boolean(isDesktop)}
          />
        </div>
        <div ref={scrollableRef} className={styles.singlesScrollable}>
          {activePebbleContent}
        </div>
      </>
    );
  }, [onTabSwitch, headers, contents, isSingleContent, activePebbleId, onPebbleClick, isDesktop]);

  return (
    <section className={classnames(styles.container, { [styles.singlesContainer]: isSingleContent })}>
      {content}
      {hasMarketBlurbs && (
        <div className={styles.blurbContainer}>
          <BlurbBar text={i18n.voidBlurbText} />
        </div>
      )}
      <PlaceFooter
        i18n={i18n}
        isPanelDisabled={isPanelDisabled}
        isPlaceDisabled={isPlaceDisabled}
        notifications={notifications}
        footerPrefix={footerPrefix}
        termsUrl={termsUrl}
        hasFreeBets={hasFreeBets}
        isFreeBetsSelected={isFreeBetsSelected}
        isFreeBetsDisabled={isFreeBetsDisabled}
        freeBetsAlertMessage={freeBetsAlertMessage}
        isSummaryDisabled={isSummaryDisabled}
        balanceAfterBet={balanceAfterBet}
        totalReturns={totalReturns}
        totalOriginalReturns={totalOriginalReturns}
        isOddsBoosted={isOddsBoosted}
        showAcceptOddsMovementAlert={showAcceptOddsMovementAlert}
        isOddsMovementOn={isOddsMovementOn}
        oddsMovementLabels={oddsMovementLabels}
        hasCTALoading={hasCTALoading}
        hasPlaceError={hasPlaceError}
        isPlaceBlocked={isPlaceBlocked}
        placeBtnLabel={placeBtnLabel}
        placeBtnSecondaryLabel={placeBtnSecondaryLabel}
        placeBtnLoadingLabel={placeBtnLoadingLabel}
        reversePlaceBtnLabels={reversePlaceBtnLabels}
        isLoggedIn={isLoggedIn}
        secondaryButton={secondaryButton}
        onFreeBetsChange={onFreeBetsChange}
        onFreeBetsRemovePress={onFreeBetsRemovePress}
        onRemoveAllPress={onRemoveAllClick}
        onPlacePress={onPlaceClick}
        onOddsMovementPreferencesChange={onOddsMovementPreferencesChange}
      />
    </section>
  );
};
